import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  RiFileTextLine,
  RiEyeLine,
  RiDeleteBin6Line,
  RiRefreshLine,
  RiErrorWarningLine,
  RiCheckboxCircleLine,
  RiFolderOpenLine,
  RiFocus3Line,
  RiCloseLine,
  RiLoader2Fill,
  RiLoader4Line,
  RiFileImageLine,
  RiFilePdfLine,
  RiFileWord2Line,
  RiFileExcel2Line,
  RiBookmarkLine,
  RiDraggable,
  RiAddLine,
  RiCloseLine as RiCloseCircleLine,
  RiEditLine,
} from 'react-icons/ri';
import { DocumentWithStatus, CriteriaAnalysis } from '@/types/document';
import Tooltip from '@/components/ui/Tooltip';
import DocumentMoveMenu from './DocumentMoveMenu';
import DocumentDownloadMenu from './DocumentDownloadMenu';
import { ValidationBadge } from './ValidationIndicator';
import CriteriaPicker from './CriteriaPicker';
import { CasesApi } from '@/api';

interface DocumentItemProps {
  document: DocumentWithStatus;
  criteria: CriteriaAnalysis[];
  allCriteria?: CriteriaAnalysis[];
  caseId?: string;
  onRetry: (documentId: string) => Promise<void>;
  onDelete: (documentId: string) => Promise<void>;
  onRename?: (documentId: string, customFileName: string) => Promise<any>;
  onPreview?: (document: DocumentWithStatus) => void;
  onDownload?: (document: DocumentWithStatus) => void;
  onMoveSuccess?: () => void;
  onValidationClick?: (document: DocumentWithStatus) => void;
  onCriteriaAssignmentSuccess?: () => void;
  className?: string;
  highlighted?: boolean;
}

const DocumentItem: React.FC<DocumentItemProps> = ({
  document,
  criteria,
  allCriteria = [],
  caseId,
  onRetry,
  onDelete,
  onRename,
  onPreview,
  onDownload,
  onMoveSuccess,
  onValidationClick,
  onCriteriaAssignmentSuccess,
  className = '',
  highlighted = false,
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [isRenamingInProgress, setIsRenamingInProgress] = useState(false);
  const [unassigningCriteriaId, setUnassigningCriteriaId] = useState<
    string | null
  >(null);

  const getStatusIcon = () => {
    switch (document.processingStatus) {
      case 'UPLOADING':
      case 'PENDING':
      case 'PROCESSING':
      case 'AI_CATEGORIZING':
      case 'OCR_PROCESSING':
        return <RiLoader2Fill className="w-4 h-4 animate-spin text-blue-500" />;
      case 'COMPLETED':
        return <RiCheckboxCircleLine className="w-4 h-4 text-green-500" />;
      case 'FAILED':
        return <RiErrorWarningLine className="w-4 h-4 text-red-500" />;
      default:
        return <RiFileTextLine className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = () => {
    switch (document.processingStatus) {
      case 'UPLOADING':
      case 'PENDING':
      case 'AI_CATEGORIZING':
      case 'OCR_PROCESSING':
      case 'PROCESSING':
        return 'bg-blue-50 border-blue-200 text-blue-700';
      case 'COMPLETED':
        return 'bg-green-50 border-green-200 text-green-700';
      case 'FAILED':
        return 'bg-red-50 border-red-200 text-red-700';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-700';
    }
  };

  const getStatusText = () => {
    // Prefer friendly label injected by transformDocument
    // @ts-ignore optional property
    if ((document as any).displayStatusLabel)
      return (document as any).displayStatusLabel;

    // Fallback legacy mapping
    switch (document.processingStatus) {
      case 'UPLOADING':
        return 'Uploading...';
      case 'PENDING':
        return 'Queued';
      case 'PROCESSING':
        return 'Processing';
      case 'AI_CATEGORIZING':
        return 'AI categorizing';
      case 'OCR_PROCESSING':
        return 'Reading file';
      case 'COMPLETED':
        return 'Ready';
      case 'FAILED':
        return 'Failed';
      default:
        return 'Unknown';
    }
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await onRetry(document.id);
    } catch (error) {
      console.error('Retry failed:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to retry document processing';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsRetrying(false);
    }
  };

  const handleDelete = async () => {
    if (confirm(`Are you sure you want to delete "${getDisplayName()}"?`)) {
      try {
        await onDelete(document.id);
      } catch (error) {
        console.error('Delete failed:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to delete document';
        alert(`Error: ${errorMessage}`);
      } finally {
        onCriteriaAssignmentSuccess?.();
      }
    }
  };

  const handleRename = async () => {
    if (!newFileName.trim()) return;

    setIsRenamingInProgress(true);
    try {
      // Use the onRename callback if provided, otherwise fall back to direct API call
      if (onRename) {
        await onRename(document.id, newFileName.trim());
      } else if (caseId) {
        await CasesApi.renameDocument(caseId, document.id, newFileName.trim());
      }
      setIsRenaming(false);
      setNewFileName('');
      // Only call onMoveSuccess if we used the direct API call (for backward compatibility)
      if (!onRename) {
        onMoveSuccess?.(); // Trigger refresh
      }
    } catch (error) {
      console.error('Rename failed:', error);
      alert('Failed to rename document');
    } finally {
      setIsRenamingInProgress(false);
    }
  };

  const openRenameModal = () => {
    // Get current name without extension
    const currentName = (
      document.customFileName ||
      document.fileName ||
      ''
    ).replace(/\.[^/.]+$/, '');
    setNewFileName(currentName);
    setIsRenaming(true);
  };

  // Get display name - prioritize customFileName over fileName
  // Removes extension and replaces underscores with spaces for readability
  const getDisplayName = () => {
    const name = document.customFileName || document.fileName || 'Untitled';
    // Remove file extension for display
    const nameWithoutExt = name.replace(/\.[^/.]+$/, '');
    // Replace underscores with spaces for better readability
    return nameWithoutExt.replace(/_/g, ' ');
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf'))
      return <RiFilePdfLine className="w-5 h-5 text-red-500" />;
    if (mimeType.includes('image'))
      return <RiFileImageLine className="w-5 h-5 text-blue-500" />;
    if (mimeType.includes('word'))
      return <RiFileWord2Line className="w-5 h-5 text-blue-600" />;
    if (mimeType.includes('excel'))
      return <RiFileExcel2Line className="w-5 h-5 text-green-600" />;
    return <RiFileTextLine className="w-5 h-5 text-gray-500" />;
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('documentId', document.id);
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <div
      draggable={document.processingStatus === 'COMPLETED'}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={`group border border-neutral-200/80 rounded-lg hover:shadow-soft-md transition-all duration-300 ${
        highlighted ? 'bg-primary-50 border-primary-200' : 'bg-white'
      } ${isDragging ? 'opacity-75 cursor-move scale-95' : ''} ${
        document.processingStatus === 'COMPLETED' ? 'cursor-move' : ''
      } ${className}`}
      title={
        document.processingStatus === 'COMPLETED'
          ? 'Drag to move to folder'
          : ''
      }
    >
      <div className="p-3 md:p-4">
        {/* Header */}
        <div className="flex items-start justify-between w-full gap-2">
          <div
            className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => {
              if (document.filePath && onPreview) {
                onPreview(document);
              }
            }}
          >
            {/* Drag handle for completed documents */}
            {document.processingStatus === 'COMPLETED' && (
              <div
                className="flex-shrink-0 cursor-move text-gray-400 hover:text-gray-600 mr-1"
                title="Drag to move to folder"
              >
                <RiDraggable className="w-4 h-4" />
              </div>
            )}

            {/* File Icon with Status Badge */}
            <div className="relative flex-shrink-0">
              <div className="w-10 h-10 bg-neutral-50 rounded-lg flex items-center justify-center border border-neutral-200/80 transition-colors group-hover:border-neutral-300">
                {document.processingStatus === 'OCR_PROCESSING' ? (
                  <RiLoader2Fill className="w-5 h-5 animate-spin text-primary-500" />
                ) : (
                  getFileIcon(document.mimeType)
                )}
              </div>
              {document.processingStatus !== 'OCR_PROCESSING' && (
                <span
                  className={`absolute -bottom-1 -right-1 rounded-full text-[10px] leading-none shadow-soft ${getStatusColor()}`}
                  title={getStatusText()}
                >
                  {getStatusIcon()}
                </span>
              )}
            </div>

            {/* File Info */}
            <div className="flex-1 min-w-0 max-w-full">
              <div className="flex flex-wrap md:flex-nowrap items-start justify-between gap-2 min-w-0">
                <div className="flex flex-col gap-1.5 min-w-0 flex-1">
                  {/* Title row */}
                  <div className="flex items-center gap-1.5 min-w-0">
                    <div
                      className="flex items-center gap-1 min-w-0 flex-1"
                      onClick={e => e.stopPropagation()}
                    >
                      {isRenaming ? (
                        <input
                          type="text"
                          value={newFileName}
                          onChange={e => setNewFileName(e.target.value)}
                          onKeyDown={e => {
                            if (
                              e.key === 'Enter' &&
                              !isRenamingInProgress &&
                              newFileName.trim()
                            ) {
                              handleRename();
                            }
                            if (e.key === 'Escape') {
                              setIsRenaming(false);
                              setNewFileName('');
                            }
                          }}
                          onBlur={() => {
                            if (!isRenamingInProgress) {
                              // Save if there's a valid new name, otherwise cancel
                              if (
                                newFileName.trim() &&
                                newFileName.trim() !==
                                  (
                                    document.customFileName ||
                                    document.fileName ||
                                    ''
                                  ).replace(/\.[^/.]+$/, '')
                              ) {
                                handleRename();
                              } else {
                                setIsRenaming(false);
                                setNewFileName('');
                              }
                            }
                          }}
                          className="text-[13px] leading-[18px] font-medium text-gray-900 bg-transparent focus:outline-none flex-1 min-w-0 px-1 py-0.5 border border-blue-500 rounded"
                          placeholder="Enter new file name"
                          autoFocus
                          disabled={isRenamingInProgress}
                        />
                      ) : (
                        <>
                          <h4
                            className="text-[13px] leading-[18px] font-medium text-gray-900 truncate px-1 py-0.5 border border-transparent rounded"
                            title={getDisplayName()}
                          >
                            {getDisplayName()}
                          </h4>
                          {caseId &&
                            document.id &&
                            document.processingStatus === 'COMPLETED' && (
                              <Tooltip content="Rename">
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    openRenameModal();
                                  }}
                                  className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-opacity"
                                  aria-label="Rename"
                                >
                                  <RiEditLine className="w-4 h-4" />
                                </button>
                              </Tooltip>
                            )}
                        </>
                      )}
                    </div>
                    {document.validationResult && (
                      <div onClick={e => e.stopPropagation()}>
                        <ValidationBadge
                          validation={document.validationResult}
                          onClick={() => onValidationClick?.(document)}
                          variant="icon"
                        />
                      </div>
                    )}
                  </div>

                  {/* Criteria badges */}
                  {(criteria.length > 0 ||
                    (caseId &&
                      document.id &&
                      document.processingStatus === 'COMPLETED')) && (
                    <div
                      className="flex flex-wrap items-center gap-1.5 ml-2"
                      onClick={e => e.stopPropagation()}
                    >
                      {criteria.map(criterion => {
                        const isUnassigning =
                          unassigningCriteriaId === criterion.criteriaId;
                        return (
                          <span
                            key={criterion.criteriaId}
                            className={`inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-lg text-[11px] font-medium bg-primary-50 text-primary-700 border border-primary-200/80 hover:bg-primary-100 transition-colors shadow-soft ${isUnassigning ? 'opacity-60' : ''}`}
                            title={
                              criterion.description || criterion.criteriaName
                            }
                          >
                            <span
                              className="truncate max-w-[150px]"
                              title={criterion.criteriaName}
                            >
                              {criterion.criteriaName}
                            </span>
                            {caseId && document.id && (
                              <button
                                onClick={async e => {
                                  e.stopPropagation();
                                  if (unassigningCriteriaId) return;
                                  setUnassigningCriteriaId(
                                    criterion.criteriaId
                                  );
                                  try {
                                    await CasesApi.unassignDocumentFromCriteria(
                                      caseId,
                                      document.id,
                                      criterion.criteriaId
                                    );
                                    onCriteriaAssignmentSuccess?.();
                                  } catch (error) {
                                    console.error(
                                      'Failed to unassign criterion:',
                                      error
                                    );
                                  } finally {
                                    setUnassigningCriteriaId(null);
                                  }
                                }}
                                disabled={!!unassigningCriteriaId}
                                className="flex-shrink-0 hover:bg-blue-200 rounded-full p-0.5 transition-colors disabled:opacity-50"
                                title="Remove criterion"
                              >
                                {isUnassigning ? (
                                  <RiLoader4Line className="w-3 h-3 animate-spin" />
                                ) : (
                                  <RiCloseCircleLine className="w-3 h-3" />
                                )}
                              </button>
                            )}
                          </span>
                        );
                      })}
                      {caseId &&
                        document.id &&
                        document.processingStatus === 'COMPLETED' && (
                          <CriteriaPicker
                            caseId={caseId}
                            documentId={document.id}
                            assignedCriteriaIds={criteria.map(
                              c => c.criteriaId
                            )}
                            availableCriteria={allCriteria}
                            onAssignSuccess={() =>
                              onCriteriaAssignmentSuccess?.()
                            }
                          />
                        )}
                    </div>
                  )}
                </div>
                {/* Actions cluster */}
                <div
                  className="flex items-center gap-1.5 flex-shrink-0 ml-1"
                  onClick={e => e.stopPropagation()}
                >
                  {document.filePath && onPreview && (
                    <Tooltip content="Preview">
                      <button
                        onClick={() => onPreview(document)}
                        className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-600 hover:text-primary-700 transition-colors"
                        aria-label="Preview"
                      >
                        <RiEyeLine className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  )}
                  {document.filePath && onDownload && caseId && (
                    <DocumentDownloadMenu
                      caseId={caseId}
                      document={document}
                      onDownloadOriginal={onDownload}
                    />
                  )}
                  {caseId &&
                    document.id &&
                    document.processingStatus === 'COMPLETED' && (
                      <Tooltip content="Move to folder">
                        <DocumentMoveMenu
                          caseId={caseId}
                          documentId={document.id}
                          documentName={getDisplayName()}
                          currentFolderId={document.folderId}
                          onMoveSuccess={onMoveSuccess}
                        />
                      </Tooltip>
                    )}
                  {document.processingStatus === 'FAILED' && (
                    <Tooltip content="Retry">
                      <button
                        onClick={handleRetry}
                        disabled={isRetrying}
                        className="p-1.5 rounded-lg hover:bg-orange-50 text-orange-600 hover:text-orange-700 disabled:opacity-50 transition-colors"
                        aria-label="Retry"
                      >
                        <RiRefreshLine
                          className={`w-4 h-4 ${isRetrying ? 'animate-spin' : ''}`}
                        />
                      </button>
                    </Tooltip>
                  )}
                  {/* Hide delete button while uploading */}
                  {document.processingStatus !== 'UPLOADING' && (
                    <Tooltip content="Delete">
                      <button
                        onClick={handleDelete}
                        className="p-1.5 rounded-lg hover:bg-error-50 text-error-600 hover:text-error-700 transition-colors"
                        aria-label="Delete"
                      >
                        <RiDeleteBin6Line className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
              {/* Meta line removed to save vertical space; only document type is shown next to the title */}
            </div>
          </div>
        </div>

        {/* Tags and Folder */}
        {showDetails &&
          ((document.tags && document.tags.length > 0) ||
            document.originalFolderPath) && (
            <div className="mt-2.5 flex flex-col sm:flex-row sm:items-center flex-wrap gap-1.5">
              {document.originalFolderPath && (
                <div className="flex items-center space-x-1 text-[11px] text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded max-w-full">
                  <RiFolderOpenLine className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">
                    Suggested: {document.originalFolderPath}
                  </span>
                </div>
              )}

              <div className="flex flex-wrap gap-1">
                {document.tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-blue-100 text-blue-800 border border-blue-200 max-w-full"
                  >
                    <span className="truncate">{tag}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default DocumentItem;
